import { get, writable } from 'svelte/store';

import { HALF_SLEEPING_TYPES, SLEEPING_TYPES } from 'src/constants';
import { get_ssid, real_id } from 'src/helpers/interface_helpers';
import type { Device } from 'src/types/Device';

import { home_data } from '../account/user';
import { devices_settings, devices_status } from './status';
import { devices } from '../lists/devices';
import { app, network, wifi_ssid } from '../session/native-bridge';

function create_device_auths() {
	const { subscribe, set, update } = writable(JSON.parse(localStorage.getItem('device_auths')) || {});

	return {
		subscribe,
		set,
		update: (id, auth) =>
			update((_auths) => {
				return { ..._auths, [id]: auth };
			}),
		remove: (id) =>
			update((_auths) => {
				delete _auths[id];
				return _auths;
			}),
		reset: () => set({}),
	};
}

export const device_auths = create_device_auths();
device_auths.subscribe((val) => localStorage.setItem('device_auths', JSON.stringify(val)));

function create_auth_locked_devices() {
	const { subscribe, set, update } = writable([]);

	return {
		subscribe,
		set,
		add: (device: string) =>
			update((auth_locked_devices) => {
				const hasCredentials = (get(device_auths) as Record<string, unknown>)?.[device];
				if (typeof hasCredentials !== 'undefined' && hasCredentials !== null) {
					return auth_locked_devices;
				}
				auth_locked_devices.push(device);
				return [...new Set(auth_locked_devices)];
			}),
		remove: (device) =>
			update((auth_locked_devices) => {
				const index_for_remove = auth_locked_devices.indexOf(device);
				if (index_for_remove >= 0) {
					auth_locked_devices.splice(index_for_remove, 1);
				}
				return auth_locked_devices;
			}),
	};
}

export const auth_locked_devices = create_auth_locked_devices();

function create_req_locked_devices() {
	const { subscribe, set, update } = writable(
		JSON.parse(sessionStorage.getItem('req_locked_devices ')) || [],
	);

	return {
		subscribe,
		set,
		add: (device_id) =>
			update((req_locked_devices) => {
				req_locked_devices.push(device_id);
				return [...new Set(req_locked_devices)];
			}),
		remove: (device_id) =>
			update((req_locked_devices) => {
				let index_for_remove;
				req_locked_devices.forEach((id, i) => {
					if (id === device_id) {
						index_for_remove = i;
					}
				});
				req_locked_devices.splice(index_for_remove, 1);
				return req_locked_devices;
			}),
	};
}

export const req_locked_devices = create_req_locked_devices();
req_locked_devices.subscribe((val) =>
	sessionStorage.setItem('req_locked_devices ', JSON.stringify(val)),
);

const _auth_unlocked_at = writable<Record<string, number>>({});

/**
 * Legacy grace-period API kept for compatibility. No longer used: we rely on
 * "try unlock behind the scenes with stored password" (handleUnauthorizedRequest) instead.
 */
export const auth_unlocked_at = {
	subscribe: _auth_unlocked_at.subscribe,
	recordUnlock: (_id: string) => {},
	isRecentlyUnlocked: (_id: string) => false,
	reset: () => _auth_unlocked_at.set({}),
	clearUnlock: (_id: string) => {},
	/**
	 * Syncs auth_locked_devices: add devices with auth_en and no stored credentials.
	 * Call periodically (e.g. after v2/socket updates).
	 */
	recheckExpiredAndReLock: () => {
		const networkStatus = get(network);
		const isWifi = networkStatus === 'wifi' || networkStatus === 'wi-fi';
		const isConnected = get(app) && isWifi;
		if (!isConnected) {
			auth_locked_devices.set([]);
			return;
		}
		const useLocal = get(home_data)?.globalLocalControl?.useLocal;
		if (!useLocal) {
			auth_locked_devices.set([]);
			return;
		}
		const phoneSsid = get(wifi_ssid);
		const statusMap = get(devices_status);
		const deviceAuths = get(device_auths) as Record<string, unknown>;
		const devicesMap = get(devices) as Record<string, Device>;
		auth_locked_devices.set([]);
		if (statusMap !== null && statusMap !== undefined && statusMap.constructor === Object) {
			for (const realId of Object.keys(statusMap)) {
				const status = statusMap[realId];
				const settings = (get(devices_settings) as Record<string, any>)?.[realId];
				if (!status?.online) continue;
				const device =
					devicesMap[realId] ??
					Object.values(devicesMap).find((d: Device) => real_id(d.id) === realId);
				const deviceType = device?.type;
				const devicesNotAllowedToUseLocal =
					deviceType != null &&
					(SLEEPING_TYPES.includes(deviceType) || HALF_SLEEPING_TYPES.includes(deviceType));

				const authEnabled =
					(settings?.DeviceInfo?.auth_en === true || settings?.auth_en === true) &&
					!devicesNotAllowedToUseLocal;
				const hasCredentials =
					deviceAuths?.[realId] !== undefined && deviceAuths?.[realId] !== null;
				if (authEnabled && !hasCredentials) {
					const deviceSsid = device ? get_ssid(settings, status, device) : undefined;
					const onSameNetwork = phoneSsid === deviceSsid;
					if (onSameNetwork) {
						auth_locked_devices.add(realId);
					}
				}
			}
		}
	},
};
